import { createContext, useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import axiosPublic from '@/api/axiosPublic';

/**
 * Admin-configured site content (LK Admin 2.0 → Settings/Experience):
 *   GET /config/public   → support contacts, social + legal links, …
 *   GET /menus/:location → header / footer menus (deeplink grammar)
 *   GET /pages/:slug     → CMS pages (Markdown)
 *   GET /seo?path=       → per-route title/description/OG
 *   POST /contact        → contact form
 * Every consumer has a hardcoded fallback, so the site renders exactly as
 * before if the API is slow or down.
 */

export const FALLBACK_SETTINGS = {
  support_phone: '+919571364889',
  support_email: 'info@localkonnect.com',
  office_address: 'Bikaner, Rajasthan',
  social_links: [
    { label: 'Facebook', url: 'https://www.facebook.com/profile.php?id=61568531426403' },
    { label: 'Instagram', url: 'https://www.instagram.com/localkonnect/' },
    { label: 'LinkedIn', url: 'https://www.linkedin.com/company/local-konnect' },
  ],
  events_category_id: 7,
  trending_searches: [],
};

const MENU_LOCATIONS = ['web_header', 'web_footer_company', 'web_footer_explore', 'web_footer_legal'];

const SiteContentContext = createContext({ settings: FALLBACK_SETTINGS, menus: {}, ready: false });

export function SiteContentProvider({ children }) {
  const [state, setState] = useState({ settings: FALLBACK_SETTINGS, menus: {}, ready: false });

  useEffect(() => {
    let cancelled = false;
    Promise.allSettled([
      axiosPublic.get('/config/public'),
      ...MENU_LOCATIONS.map((loc) => axiosPublic.get(`/menus/${loc}`)),
    ]).then(([config, ...menus]) => {
      if (cancelled) return;
      const remote = config.status === 'fulfilled' ? config.value.data?.data?.settings ?? {} : {};
      // Blank remote values keep the fallback (a key the admin hasn't filled in yet).
      const settings = { ...FALLBACK_SETTINGS };
      for (const [k, v] of Object.entries(remote)) {
        if (v !== '' && v !== null && !(Array.isArray(v) && v.length === 0)) settings[k] = v;
      }
      const menuMap = {};
      MENU_LOCATIONS.forEach((loc, i) => {
        const r = menus[i];
        const items = r.status === 'fulfilled' ? r.value.data?.data ?? [] : [];
        if (items.length) menuMap[loc] = items;
      });
      setState({ settings, menus: menuMap, ready: true });
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return <SiteContentContext.Provider value={state}>{children}</SiteContentContext.Provider>;
}

export const useSiteSettings = () => useContext(SiteContentContext).settings;

/** Admin menu for a location, or `fallback` when none is configured. */
export function useMenu(location, fallback) {
  const { menus } = useContext(SiteContentContext);
  return menus[location] ?? fallback;
}

/**
 * Deeplink grammar → website link. Returns { to } for internal routes or
 * { href } for external ones. Unknown shapes return null (item is skipped).
 */
export function resolveLink(link) {
  if (!link) return null;
  if (link === 'events') return { to: '/', events: true };
  if (link === 'referrals') return { to: '/my-bookings' };
  if (link.startsWith('/')) return { to: link };
  const [kind, ...rest] = link.split(':');
  switch (kind) {
    case 'page':
      return { to: `/p/${rest[0]}` };
    case 'service':
      return { to: `/event/${rest[0]}` };
    case 'provider':
      return { to: `/venue/${rest[1] ?? rest[0]}` };
    case 'category':
      return { to: `/?category=${rest[0]}` };
    case 'url': {
      const url = rest.join(':');
      return /^https?:\/\//.test(url) ? { href: url } : null;
    }
    default:
      return null;
  }
}

export async function fetchPage(slug) {
  const res = await axiosPublic.get(`/pages/${encodeURIComponent(slug)}`, { params: { audience: 'web' } });
  return res.data?.data ?? null;
}

export async function submitContact(payload) {
  const res = await axiosPublic.post('/contact', { ...payload, source: 'website' });
  return res.data;
}

function setMeta(attr, key, value) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!value) {
    el?.remove();
    return;
  }
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', value);
}

const DEFAULT_TITLE = typeof document !== 'undefined' ? document.title : 'LocalKonnect';

/**
 * Applies admin SEO (/seo?path=) on every route change. Pages that know better
 * (an event's own name) can still set document.title after this runs.
 * Note: this is client-side — crawlers that don't run JS won't see it until
 * the site is server-rendered (ADMIN_V2_PLAN §5.6).
 */
export function SeoManager() {
  const { pathname } = useLocation();
  useEffect(() => {
    let cancelled = false;
    axiosPublic
      .get('/seo', { params: { path: pathname } })
      .then((res) => {
        if (cancelled) return;
        const seo = res.data?.data;
        document.title = seo?.title || DEFAULT_TITLE;
        setMeta('name', 'description', seo?.description);
        setMeta('property', 'og:title', seo?.title);
        setMeta('property', 'og:description', seo?.description);
        setMeta('property', 'og:image', seo?.ogImage);
        setMeta('name', 'robots', seo?.noindex ? 'noindex,nofollow' : null);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [pathname]);
  return null;
}
